import { Link } from "react-router-dom";
import { useId } from "react";

/**
 * CredNX brand logo.
 *
 * The "C" is a standalone icon with a built-in checkmark formed by the
 * crossing strokes. Combined with the "REDNX" wordmark on the right, it reads
 * as "CREDNX".
 *
 * Colors are mapped to the Editorial Forest theme tokens so the logo flips
 * correctly between light and dark modes:
 *   • Main C body          → primary (deep forest)
 *   • Front/check stroke   → accent (lime — makes the checkmark read as "verified")
 *   • Secondary details    → primary-glow (mid forest)
 *   • Wordmark             → currentColor (inherits from text color)
 *
 * @param {"full" | "mark"} variant  Show full lockup or icon-only.
 * @param {number}          height   Rendered height in px (width follows aspect).
 */
export function Logo({ variant = "full", height = 28, className = "" }) {
    const uid = useId();
    const gId1 = `crednx-lg-${uid}-1`;
    const gId2 = `crednx-lg-${uid}-2`;

    const isFull = variant === "full";
    const aspect = isFull ? 405 / 84 : 83 / 83;
    const viewBox = isFull ? "0 0 405 84" : "0 0 83 83";
    const width = Math.round(height * aspect);

    return (
        <Link
            to="/"
            className={`inline-flex items-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm ${className}`}
            aria-label="CredNX home"
            data-testid="brand-logo"
        >
            <svg
                width={width}
                height={height}
                viewBox={viewBox}
                xmlns="http://www.w3.org/2000/svg"
                role="img"
            >
                <defs>
                    {/* Gradient for the right vertical sweep of the C */}
                    <linearGradient id={gId1} x1="67" y1="37" x2="67" y2="68" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="hsl(var(--primary-glow))" />
                        <stop offset="100%" stopColor="hsl(var(--primary))" />
                    </linearGradient>
                    {/* Gradient for the top sweep of the C */}
                    <linearGradient id={gId2} x1="17" y1="17" x2="65" y2="22" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="hsl(var(--primary-glow))" />
                        <stop offset="60%" stopColor="hsl(var(--primary))" />
                        <stop offset="100%" stopColor="hsl(var(--primary) / 0.9)" />
                    </linearGradient>
                </defs>

                {/* ─── Icon: the "C" with built-in checkmark ─── */}
                <g
                    className="origin-center transition-transform duration-500 group-hover:-rotate-[6deg]"
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                >
                    {/* 1. Front bright stroke — the "check tick" visual — rendered in lime */}
                    <path
                        d="M69.0322 67.6021C62.3308 63.2287 55.075 60.4683 55.075 60.4683C51.9488 61.9205 48.3736 62.0757 45.1531 60.9339C54.0385 69.4035 61.9095 70.8447 67.1809 69.4146C67.8183 68.8326 68.4391 68.2284 69.0322 67.6021Z"
                        fill="hsl(var(--accent))"
                    />
                    {/* 2. Main C body — deep forest */}
                    <path
                        d="M40.1256 57.6911C35.0483 52.4309 29.4332 46.4888 24.5222 41.4336C20.3982 37.1932 20.3483 30.4585 24.4279 26.1738C25.8968 24.6273 27.7149 23.613 29.6383 23.1196C16.3684 24.1284 9.61156 31.2401 5.06632 38.773C4.94992 40.1144 4.90558 41.478 4.93883 42.8582C5.3989 62.5856 21.4901 78.6713 41.2176 79.1369C51.2337 79.3753 60.3685 75.6448 67.1864 69.4201C62.1368 69.3425 54.4708 67.7128 45.1586 60.9393C43.3128 60.2853 41.589 59.2044 40.1312 57.6967L40.1256 57.6911Z"
                        fill="hsl(var(--primary))"
                    />
                    {/* 3. Right vertical sweep — forest gradient */}
                    <path
                        d="M79.2978 41.9602C79.2978 40.2918 79.1869 38.6455 78.9707 37.0381C72.8735 43.3515 65.3406 51.1117 58.9717 57.6967C57.8188 58.8885 56.494 59.8142 55.075 60.4738C55.075 60.4738 60.2244 65.3904 69.0322 67.6076C75.39 60.9338 79.2978 51.9099 79.2978 41.9658V41.9602Z"
                        fill={`url(#${gId1})`}
                    />
                    {/* 4. Top-right curve — mid forest */}
                    <path
                        d="M67.6742 17.4381C68.356 16.7231 68.3338 15.5812 67.6132 14.8994C61.261 8.90746 52.7913 5.13824 43.4459 4.80566C44.6819 5.24356 54.0329 11.3297 67.2806 17.8483L67.6742 17.4326V17.4381Z"
                        fill="hsl(var(--primary-glow))"
                    />
                    {/* 5. Top sweep — gradient */}
                    <path
                        d="M30.9576 22.8482C30.8966 22.8593 30.8356 22.8704 30.7691 22.887C31.3068 22.8094 31.85 22.754 32.3987 22.754C35.1203 22.754 37.853 23.7573 39.9815 25.786L48.3292 33.7291C49.4489 34.7933 51.2171 34.749 52.2814 33.6293L67.2751 17.8596C58.6114 10.3211 44.6764 5.25484 43.4403 4.81695C42.9969 4.80032 42.5535 4.78369 42.11 4.78369C25.6363 4.78369 11.6736 15.4983 6.78466 30.3312C23.1364 21.8616 30.952 22.8538 30.952 22.8538L30.9576 22.8482Z"
                        fill={`url(#${gId2})`}
                    />
                    {/* 6. Small detail at top-left — darkest forest */}
                    <path
                        d="M29.6326 23.1142C30.0095 23.02 30.3864 22.9424 30.7689 22.8814C30.8299 22.8703 30.8908 22.8537 30.9574 22.8426C30.9574 22.8426 21.7616 17.8428 6.79 30.3201C5.90313 33.0139 5.31003 35.8409 5.0606 38.762C10.0936 32.637 18.1254 25.6085 29.6326 23.1086V23.1142Z"
                        fill="hsl(var(--primary-deep))"
                    />
                </g>

                {/* ─── Wordmark: "REDNX" ─── */}
                {isFull && (
                    <g>
                        {/* R */}
                        <path
                            d="M86.6253 77.1964V8.38623H115.05C120.202 8.38623 124.648 9.31579 128.388 11.1749C132.152 13.0117 135.052 15.6547 137.091 19.1042C139.129 22.5313 140.148 26.5967 140.148 31.3005C140.148 36.0716 139.106 40.1258 137.023 43.4633C134.94 46.7784 131.984 49.3095 128.153 51.0566C124.323 52.7813 119.787 53.6437 114.546 53.6437H96.5705V40.5402H111.455C113.963 40.5402 116.058 40.2154 117.738 39.5658C119.44 38.8939 120.728 37.8859 121.602 36.5419C122.475 35.1756 122.912 33.4285 122.912 31.3005C122.912 29.1726 122.475 27.4143 121.602 26.0256C120.728 24.6144 119.44 23.5616 117.738 22.8673C116.035 22.1505 113.941 21.7921 111.455 21.7921H103.257V77.1964H86.6253ZM125.365 45.748L142.5 77.1964H124.357L107.557 45.748H125.365Z"
                            fill="currentColor"
                        />
                        {/* E */}
                        <path
                            d="M149.27 77.1964V8.38623H197.249V21.8929H165.901V36.0044H194.796V49.5446H165.901V63.6897H197.249V77.1964H149.27Z"
                            fill="currentColor"
                        />
                        {/* D */}
                        <path
                            d="M233.099 77.1964H207.665V8.38623H233.065C240.076 8.38623 246.113 9.76378 251.175 12.5189C256.26 15.2516 260.179 19.1938 262.935 24.3456C265.69 29.475 267.067 35.6124 267.067 42.7577C267.067 49.9254 265.69 56.0852 262.935 61.237C260.202 66.3888 256.293 70.3422 251.209 73.0973C246.124 75.83 240.087 77.1964 233.099 77.1964ZM224.296 63.0177H232.461C236.313 63.0177 239.572 62.3681 242.238 61.069C244.926 59.7475 246.953 57.6083 248.319 54.6516C249.708 51.6726 250.402 47.7079 250.402 42.7577C250.402 37.8075 249.708 33.8652 248.319 30.931C246.93 27.9743 244.881 25.8464 242.171 24.5472C239.483 23.2257 236.168 22.5649 232.225 22.5649H224.296V63.0177Z"
                            fill="currentColor"
                        />
                        {/* N */}
                        <path
                            d="M334.466 8.38623V77.1964H326.402L288.906 23.1697H288.234V77.1964H279.902V8.38623H287.966L325.596 62.5473H326.268V8.38623H334.466Z"
                            fill="currentColor"
                        />
                        {/* X accent stroke #1 — lime highlight */}
                        <path
                            d="M341 77L346.421 77L368 43.5L346.421 8L341.115 8L362.688 43L341 77Z"
                            fill="hsl(var(--accent))"
                        />
                        {/* X accent stroke #2 — lime highlight */}
                        <path
                            d="M349 77L359.204 77L381 43.5L359.204 8L349.217 8L371 43L349 77Z"
                            fill="hsl(var(--accent))"
                        />
                        {/* X */}
                        <path
                            d="M359.171 8.38574L376.912 37.0118L377 38L395.189 8.38574H405L383.327 43L405 77.1959H395.189L377.449 49.1074H376.912L359.171 77.1959H349.361L371.5 43L349.361 8.38574H359.171Z"
                            fill="currentColor"
                        />
                    </g>
                )}
            </svg>
        </Link>
    );
}
